/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col, message, Button, Tooltip, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import ReportModernViewWrapper from "../ReportModernViewWrapper";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadForceBagReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const ForceBagReport = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedPremises, setSelectedPremises] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [bagAwbSearch, setBagAwbSearch] = useState("");
    const [shipmentAwbSearch, setShipmentAwbSearch] = useState("");
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Force Bag Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);

    const columns = [
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Hub Name", dataIndex: "premiseName", key: "premiseName" },
        {
            title: "Bag Barcode No", dataIndex: "bagAwbNumber", key: "bagAwbNumber",
            render: (text) => {
                return (
                    <span style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }} key={text} onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}>{text}</span>
                );
            }
        },
        {
            title: "Shipment AWB Numbers", dataIndex: "shipmentAwbNumbers", key: "shipmentAwbNumbers",
            render: (text, record) => {
                return (
                    <span>
                        {record?.shipmentAwbNumbers?.map(item => (
                            <span style={{ color: "#1890ff", cursor: "pointer" }} key={item} onClick={() => {
                                const url = `/shipment-tracker?awb=${item}`;
                                window.open(url, "_blank");
                            }}>{item}{record?.shipmentAwbNumbers?.length > 1 ? ", " : ""}</span>
                        ))}
                    </span>
                );
            }
        },
        { title: "User Name", dataIndex: "userName", key: "userName" }
    ];

    useEffect(() => {
        (async () => {
            try {
                const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userId = JSON.parse(localStorage.getItem("userId") || "{}");
                setHubUser(user?.roles?.[0]);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (e) {
                message.error("Failed to load hubs");
            }
        })();
    }, []);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedPremises([newActiveHubId]);
            } else {
                setSelectedPremises(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];

            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedPremises([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedPremises([]);
            } else {
                setSelectedPremises(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    useEffect(() => {
        if (!hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedPremises.length === 0) return;
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        const timer = setTimeout(() => {
            fetchReport();
        }, 200);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [hubUser, dateRange, selectedPremises, pagination.current, pagination.pageSize, bagAwbSearch, shipmentAwbSearch]);

    const fetchReport = async () => {
        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("force-bag-report");

        try {
            setLoading(true);

            // Resolve Hubs (selectedPremises) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedPremiseIds = selectedPremises?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedPremises;

            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                premiseIds: resolvedPremiseIds && resolvedPremiseIds.length > 0 ? resolvedPremiseIds.join(",") : undefined,
                page: (pagination.current || 1) - 1,
                size: pagination.pageSize || 10,
                bagAwbNumber: bagAwbSearch && bagAwbSearch.trim() ? bagAwbSearch.trim() : undefined,
                shipmentAwbNumber: shipmentAwbSearch && shipmentAwbSearch.trim() ? shipmentAwbSearch.trim() : undefined
            };
            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getForceBagReport(params, { signal });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                console.log(`🔥 Request ${requestId} completed successfully - updating state`);
                setReportData(data || []);
                setLastsynced(lastRefreshedAt);
                setNextScheduledRefresh(nextScheduledRefresh);
                setPagination(prev => ({ ...prev, total: total || 0 }));
            }
        } catch (err) {
            // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
            if (err.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (isLatestRequest(requestId)) {
                message.error("Failed to fetch Force Bag data");
            }
        } finally {
            // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (p) => {
        setPagination(prev => ({ ...prev, current: p.current, pageSize: p.pageSize }));
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range before downloading");
            return;
        }
        setDownloadLoading(true);
        try {
            // Resolve Hubs (selectedPremises) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedPremiseIds = selectedPremises?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedPremises;

            await downloadForceBagReport({
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                premiseIds: resolvedPremiseIds
            });
        } catch (e) {
            message.error("Failed to initiate download");
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <ReportModernViewWrapper
            title="Force Bag Report"
            newReportUrl="/reports/force-bag-report/"
            lastsynced={lastsynced}
            nextScheduledRefresh={nextScheduledRefresh}
        >
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} lg={4} xl={4}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            placeholder={["From Date", "To Date"]}
                            value={dateRange}
                            onChange={(val) => setDateRange(val)}
                            allowClear={false}
                        />
                    </Col>
                    <Col xs={24} lg={4} xl={4}>
                        <div className="filter-label">Hubs</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedPremises, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedPremises}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            dropdownStyle={{ maxHeight: 256, overflow: "auto" }}
                        />
                    </Col>
                    <Col xs={24} lg={4} xl={4}>
                        <div className="filter-label">Bag AWB Number(s)</div>
                        <Input
                            className="custom-calendar"
                            placeholder="Enter bag AWB numbers, comma separated"
                            value={bagAwbSearch}
                            onChange={(e) => {
                                setBagAwbSearch(e.target.value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} lg={4} xl={4}>
                        <div className="filter-label">Shipment AWB Number(s)</div>
                        <Input
                            className="custom-calendar"
                            placeholder="Enter shipment AWB numbers, comma separated"
                            value={shipmentAwbSearch}
                            onChange={(e) => {
                                setShipmentAwbSearch(e.target.value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} lg={4} xl={4}>
                        <div className="filter-label">&nbsp;</div>
                        <Tooltip title="Download Force Bag Report">
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                loading={downloadLoading}
                                disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                            >
                                Download
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>

                <div className="table-section">
                    <TableComponent
                        columns={columns}
                        dataSource={reportData}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        rowKey={(row) => `${row.bagBarcodeNo || row.shipment || row.premiseName}-${row.date}`}
                        scroll={{ x: 1000 }}
                    />
                </div>
            </Card>
        </ReportModernViewWrapper>
    );
};

export default ForceBagReport;


